import { Observable } from 'rxjs';

import { AgentDisplay, AgentReplyResult, MessageDisplay } from '../models/connect.models';

/**
 * Abstract HTTP adapter for Connect module's external API calls.
 *
 * The consuming application must provide a concrete implementation
 * that bridges to its own HTTP / backend layer with access to environment config.
 *
 * Register the implementation at the app root level:
 * ```ts
 * { provide: ConnectHttpAdapter, useClass: AppConnectHttpAdapter }
 * ```
 */
export abstract class ConnectHttpAdapter {
  /** Get base URL for Identity API */
  abstract getIdentityApiUrl(): string;

  /** Get base URL for Document Upload API */
  abstract getDocumentUploadApiUrl(): string;

  /** Build a browser-safe document URL using the host app's standard proxy route. */
  abstract buildDocumentUrl(fileUrl: string): string;

  /** Build the media storage source URL from a relative attachment path. */
  abstract buildMediaSourceUrl(relativePath: string): string;

  /** Download a document through the host app's authenticated HTTP pipeline. */
  abstract downloadDocument(documentUrl: string): Observable<Blob>;

  /** HTTP GET to Identity API */
  abstract getFromIdentity<T>(path: string): Observable<T>;

  /** Fetch all AI agents available to the current tenant. */
  abstract listAgents(tenantId: string): Observable<AgentDisplay[]>;

  /** Execute a one-to-one AI agent query using the selected bot token. */
  abstract queryAgent(botToken: string, query: string, conversationId?: string | null): Observable<AgentReplyResult>;

  /** Fetch conversation history from GeraltAI for a given bot token and conversation ID. */
  abstract getAgentConversation(botToken: string, conversationId: string): Observable<MessageDisplay[]>;

  /**
   * Register a bot in the Chat service. Idempotent — returns the same GUID for the same botToken.
   * Calls POST /api/Bot/savebotdetailsandoptions with id=null to get/create the Chat service GUID.
   */
  abstract registerBotInChatService(botToken: string, botExternalId: string): Observable<{ botChatId: string }>;

  /**
   * Link a registered bot to a user in the Chat service.
   * Calls POST /api/Bot/savebotforchat.
   */
  abstract linkBotToUser(botChatId: string, userId: string): Observable<any>;

  /**
   * Retrieve paginated bot chat messages from the Chat service.
   * Calls POST /api/Bot/getallpaginatedbotchatsbyuseridandbotid.
   */
  abstract getBotChatMessages(userId: string, botChatId: string, pageNumber?: number, pageSize?: number): Observable<MessageDisplay[]>;

  /** Upload file to Document Upload API */
  abstract uploadFile(file: File, tenantId: string, folder: string): Observable<{ fileUrl: string; fileName: string }>;
}
